/**
 * Distance -> frequency mapping.
 *
 * A theremin's pitch response is exponential in hand position, which is also how human
 * pitch perception works: equal distances should give equal *musical intervals*, not equal
 * Hz. So the normalized control value is mapped geometrically between min and max.
 */

import { clamp, clamp01, normalizeRange } from '../tracking/types';

export const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
] as const;

/** MIDI note 69 = A4 = 440 Hz. */
const A4_MIDI = 69;
const A4_HZ = 440;

export interface PitchRangeConfig {
  minFrequency: number;
  maxFrequency: number;
}

/**
 * Map a normalized control value in [0,1] to a frequency.
 *
 * `frequency = min * (max / min) ** normalized`
 */
export function normalizedToFrequency(normalized: number, config: PitchRangeConfig): number {
  const min = Math.max(1, config.minFrequency);
  const max = Math.max(min * 1.0001, config.maxFrequency);
  return min * Math.pow(max / min, clamp01(normalized));
}

/** Inverse of {@link normalizedToFrequency}; useful for tests and for seeding the UI. */
export function frequencyToNormalized(frequency: number, config: PitchRangeConfig): number {
  const min = Math.max(1, config.minFrequency);
  const max = Math.max(min * 1.0001, config.maxFrequency);
  const f = clamp(frequency, min, max);
  return Math.log(f / min) / Math.log(max / min);
}

export interface DistanceMappingOptions {
  /** Distance at which the control reads 1 (closest to the antenna). */
  near: number;
  /** Distance at which the control reads 0. */
  far: number;
  /**
   * 0.25 – 4. Values above 1 shrink the usable band around `near` (more sensitive: a small
   * hand movement covers more of the range); values below 1 widen it.
   */
  sensitivity: number;
  invert: boolean;
}

/**
 * Convert a hand-to-antenna distance (isotropic screen units) into a normalized control
 * value where **1 means "closest to the antenna"**, matching a real theremin's pitch rod.
 */
export function distanceToPitchNormalized(
  distance: number,
  options: DistanceMappingOptions,
): number {
  const near = Math.max(0, options.near);
  const sensitivity = clamp(options.sensitivity, 0.25, 4);
  const span = Math.max(1e-4, (options.far - near) / sensitivity);
  const raw = 1 - normalizeRange(distance, near, near + span);
  return options.invert ? 1 - raw : raw;
}

/** Nearest equal-tempered note plus the signed deviation in cents. */
export interface NoteReading {
  /** e.g. "A3". */
  name: string;
  /** Pitch class name without the octave, e.g. "A". */
  pitchClass: string;
  octave: number;
  midi: number;
  /** -50..50 */
  cents: number;
}

const SILENT_NOTE: NoteReading = {
  name: '—',
  pitchClass: '—',
  octave: 0,
  midi: 0,
  cents: 0,
};

export function frequencyToNote(frequency: number): NoteReading {
  if (!Number.isFinite(frequency) || frequency <= 0) return SILENT_NOTE;

  const exactMidi = A4_MIDI + 12 * Math.log2(frequency / A4_HZ);
  const midi = Math.round(exactMidi);
  const cents = Math.round((exactMidi - midi) * 100);
  // MIDI note 0 is C-1, so the octave is offset by one.
  const octave = Math.floor(midi / 12) - 1;
  const pitchClass = NOTE_NAMES[((midi % 12) + 12) % 12] ?? '—';
  return { name: `${pitchClass}${octave}`, pitchClass, octave, midi, cents };
}

export function midiToFrequency(midi: number): number {
  return A4_HZ * Math.pow(2, (midi - A4_MIDI) / 12);
}

/**
 * Snap a frequency to the nearest semitone. Not used by default (a theremin is continuous)
 * but exposed so a future "scale lock" can reuse the same maths.
 */
export function quantizeToSemitone(frequency: number): number {
  if (!Number.isFinite(frequency) || frequency <= 0) return frequency;
  return midiToFrequency(Math.round(A4_MIDI + 12 * Math.log2(frequency / A4_HZ)));
}
